import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Chip,
  InputLabel,
  TextField,
} from '@mui/material';
import Downshift from "downshift";
import { styled } from '@mui/material/styles';
import { AlertTriangle } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

const CustomDiv = styled('div')(({ theme }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    fontSize: 14,
    minHeight: "92px"
  },
  '& .MuiChip-root':{
    margin: theme.spacing(0.5),
    borderRadius: 8
  },
  '& #downshift-multiple-input':{
    flex: 1,
    padding: theme.spacing(0.5),
    minWidth: "152px",
    lineHeight: "32px",
    minHeight: "24px"
  },
  '.invalid-chip': {
    borderColor: "#d32f2f"
  }
}));

const useStyles = makeStyles()(theme => ({
  metalRoot: {
    backgroundColor: 'var(--neutral-1)',
    minHeight: '100%',
    [theme.breakpoints.up('md')]: {
      padding: `0 ${theme.spacing(4)} ${theme.spacing(0.5)}`
    }
  },
  selectLabel: {
    color: "var(--colors-text-text-secondary-700, #CDD5DF)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    marginTop: "8px",
    marginBottom: "8px"
  }
}))

export default function EmailChipsInput({ ...props }) {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { handleSelectedEmails, setEmailListError, selectedEmails, ...other } = props;
  const [ inputValue, setInputValue ] = useState("");
  const [ selectedItem, setSelectedItem ] = useState(selectedEmails || []);
  const [ inValidEmailCount, setInvalidEmailCount ] = useState(0)

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const newSelectedItem = [...selectedItem];
      const duplicatedValues = newSelectedItem.indexOf(
        event.target.value.trim()
      );

      if (duplicatedValues !== -1) {
        setInputValue("");
        return;
      }
      if (!event.target.value.replace(/\s/g, "").length) return;

      newSelectedItem.push(event.target.value.trim());
      setSelectedItem(newSelectedItem);
      setInputValue("");
    }
    else if(event.key === ","){
      const newSelectedItem = [...selectedItem];
      const duplicatedValues = newSelectedItem.indexOf(
        event.target.value.trim()
      );

      if (duplicatedValues !== -1) {
        setInputValue("");
        return;
      }
      if (!event.target.value.replace(/\s/g, "").length) return;

      newSelectedItem.push(event.target.value.trim());
      setSelectedItem(newSelectedItem);
      setInputValue("");
    }
    if (
      selectedItem.length &&
      !inputValue.length &&
      event.key === "Backspace"
    ) {
      setSelectedItem(selectedItem.slice(0, selectedItem.length - 1));
    }
  }
  
  const handleChange = (item) => {
    let newSelectedItem = [...selectedItem];
    if (newSelectedItem.indexOf(item) === -1) {
      newSelectedItem = [...newSelectedItem, item];
    }
    setInputValue("");
    setSelectedItem(newSelectedItem);
  }

  const handleDelete = (item) => () => {
    const newSelectedItem = [...selectedItem];
    newSelectedItem.splice(newSelectedItem.indexOf(item), 1);
    setSelectedItem(newSelectedItem);
  };

  const handleInputChange = (event) => {
    if(event.target.value === ",") return
    if(event.target.value.includes(",")){
      let flattenItems = [...selectedItem, event.target.value.replace(" ","").split(",")].flat(Infinity)
      setInputValue('')
      setSelectedItem(flattenItems)
      return
    }
    setInputValue(event.target.value);
  }

  const isValidEmail = (email) =>{
    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g
    return regex.test(email)
  }

  useEffect(()=>{
    const elements = document.querySelectorAll('.invalid-chip');
    const count = elements.length;
    if(count > 0 || !selectedItem.length){
      setEmailListError(true)
    }
    else {
      setEmailListError(false)
    }
    handleSelectedEmails(selectedItem)
    setInvalidEmailCount(count)
  },[selectedItem.length])



  return (
    <Box sx={{maxWidth: "100%", overflow: "hidden"}}>
      <Downshift
        style={{
          maxWidth: "100%",
          overflow: "hidden"
        }}
        id="downshift-multiple"
        inputValue={inputValue}
        onChange={handleChange}
        selectedItem={selectedItem}
      >
        {({ getInputProps }) => {
          const { onBlur, onChange, onFocus, ...inputProps } = getInputProps({
            onKeyDown: handleKeyDown,
            
          });
          return (
            <div>
              <CustomDiv>
                <TextField
                  inputProps={{
                    onBlur: () => {
                      if(inputValue){
                        let flattenItems = [...selectedItem, inputValue]
                        setInputValue('')
                        setSelectedItem(flattenItems)
                        return
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: selectedItem.map((item,index) => (
                      <Chip
                        className={`${isValidEmail(item) ? 'valid-chip' : 'invalid-chip'}`}
                        key={index}
                        tabIndex={-1}
                        label={item}
                        variant="outlined" 
                        size="small" 
                        onDelete={handleDelete(item)}
                        disabled={props.disabled}
                      />
                    )),
                    onChange: event => {
                      handleInputChange(event);
                      onChange(event);
                    },
                    onFocus
                  }}
                  {...other}
                  {...inputProps}
                />
                <InputLabel disabled className={classes.selectLabel} style={{lineHeight: "16px"}}>{t('commaSeparatedEmailAddresses')}</InputLabel>
                <InputLabel disabled className={classes.selectLabel} style={{lineHeight: "16px"}}>At least one email address is required.</InputLabel>
                {
                  !!inValidEmailCount && <div
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        flexDirection: 'row',
                        width: "inherit",
                        alignItems: 'center',
                        gap: '10px',
                        padding: 'var(--space-s)',
                        margin: 'var(--space-s) 0',
                        border: '1px solid #333339',
                        borderRadius: 'var(--radius-xl, 12px)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center'}}>
                        <Avatar sx={{ bgcolor: '#DC6803' }}>
                          <AlertTriangle
                            style={{
                              color: '#FEDF89',
                              minWidth: 'var(--space-m)',
                            }}
                            size={22}
                          />
                        </Avatar>
                        <span
                          className="m-text"
                          style={{ color: 'var(--color-text-default)', margin: '0 10px' }}
                        >
                          There {inValidEmailCount === 1 ? 'is' : 'are' } {inValidEmailCount} invalid email {inValidEmailCount === 1 ? 'address' : 'addresses'}.
                        </span>
                      </div>
                    </div>
                }
              </CustomDiv>
            </div>
          );
        }}
      </Downshift>
    </Box>
  );
}
